import { Text, UnstyledButton } from '@/shared/ui';
import type { LucideIcon } from 'lucide-react';
import { PlanIcon } from './plan-tab.icons';

export interface StripFact {
  key: string;
  icon: LucideIcon;
  label: string;
  actionLabel?: string;
  onAction?: () => void;
}

interface Props {
  planName: string;
  price: string;
  facts: StripFact[];
}

const LINK_STYLE = {
  background: 'none',
  border: 'none',
  padding: 0,
  fontSize: 'var(--text-meta)',
  fontWeight: 'var(--font-semibold)',
  color: 'var(--seed-base)',
  textDecoration: 'underline',
  textUnderlineOffset: '3px',
  cursor: 'pointer',
  whiteSpace: 'nowrap',
} as const;

/**
 * The administrative facts, compressed into one line.
 *
 * Deliberately not a SettingsBlock: a block would add the header band this
 * redesign exists to delete. Plan, price and three facts, two of which carry
 * their action inline — that is all the administrative side is allowed to
 * occupy before the usage the tab is actually about.
 *
 * @param props - Component props.
 * @returns The subscription strip.
 */
export function SubscriptionStrip({ planName, price, facts }: Props) {
  return (
    <div
      className="flex items-center gap-(--space-4) flex-wrap"
      data-testid="subscription-strip"
      style={{
        padding: 'var(--space-3-5) var(--space-4)',
        border: '1px solid var(--border-subtle)',
        borderRadius: 'var(--radius-lg)',
        background: 'var(--surface-raised)',
      }}
    >
      <div className="flex items-baseline gap-(--space-2) min-w-0">
        <Text component="span" style={{
          fontSize: 'var(--text-app-section)', fontWeight: 'var(--font-bold)',
          color: 'var(--text-primary)', letterSpacing: 'var(--tracking-tight)',
        }}>
          {planName}
        </Text>
        <Text component="span" style={{
          fontSize: 'var(--text-body-sm)', color: 'var(--text-secondary)',
          fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap',
        }}>
          {price}
        </Text>
      </div>
      <Text component="span" style={{
        width: '1px', height: 'var(--space-5)',
        background: 'var(--border-subtle)', flex: 'none',
      }} />
      {facts.map((fact) => (
        <div key={fact.key} className="flex items-center gap-(--space-1-5) min-w-0">
          <Text component="span" className="flex" style={{ flex: 'none', color: 'var(--text-tertiary-soft)' }}>
            <fact.icon size={14} strokeWidth={1.75} aria-hidden />
          </Text>
          <Text component="span" style={{
            fontSize: 'var(--text-body-sm)', color: 'var(--text-secondary)',
            whiteSpace: 'nowrap',
          }}>
            {fact.label}
          </Text>
          {fact.actionLabel && fact.onAction && (
            <UnstyledButton
              onClick={fact.onAction}
              data-testid={`strip-action-${fact.key}`}
              style={LINK_STYLE}
            >
              {fact.actionLabel}
            </UnstyledButton>
          )}
        </div>
      ))}
    </div>
  );
}

export { PlanIcon };
